"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatAncestryPath = exports.generateViewSelector = exports.ViewSelectorConfig = exports.ALL_SELECTOR_PROPERTIES = void 0;
const textNorm_1 = require("./textNorm");
const tokenEscape_1 = require("./tokenEscape");
exports.ALL_SELECTOR_PROPERTIES = new Set([
    'className',
    'role',
    'accessibilityIdentifier',
    'viewIdResourceName',
    'label',
    'contentDescription',
    'accessibilityTraits'
]);
const isSelectorProperty = (value) => exports.ALL_SELECTOR_PROPERTIES.has(value);
class ViewSelectorConfig {
    identityBearingTextOnly;
    properties;
    constructor(includedProperties = exports.ALL_SELECTOR_PROPERTIES, identityBearingTextOnly = false) {
        this.identityBearingTextOnly = identityBearingTextOnly;
        const properties = new Set(includedProperties);
        const unknown = [...properties]
            .filter(prop => !isSelectorProperty(prop))
            .sort();
        if (unknown.length > 0) {
            throw new Error(`Unknown selector properties: ${JSON.stringify(unknown)}. ` +
                `Valid properties: ${JSON.stringify([...exports.ALL_SELECTOR_PROPERTIES].sort())}`);
        }
        this.properties = properties;
    }
    includes(prop) {
        return this.properties.has(prop);
    }
    static full() {
        return new ViewSelectorConfig(exports.ALL_SELECTOR_PROPERTIES);
    }
    static ancestry() {
        return new ViewSelectorConfig([
            'className',
            'role',
            'accessibilityIdentifier',
            'viewIdResourceName',
            'label',
            'contentDescription'
        ], true);
    }
}
exports.ViewSelectorConfig = ViewSelectorConfig;
const LABEL_MAX_CODEPOINTS = 30;
const LABEL_TRUNCATED_CODEPOINTS = 27;
/**
 * Decision: short labels are treated as identity-bearing, longer ones as
 * content-bearing. A container's own label is typically a concatenation of its
 * children's, so past `LABEL_MAX_CODEPOINTS` it tracks what the container holds
 * rather than which container it is.
 *
 * Delegating to `shape` also rejects values that are short but still content:
 * a URL, date, time or bare number tracks what the screen currently shows
 * rather than which screen it is, so a counter or timestamp never reaches the
 * selector regardless of length.
 */
const isIdentityBearingText = (value) => (0, textNorm_1.shape)(String(value ?? '').trim(), LABEL_MAX_CODEPOINTS) === 'Short';
/**
 * Counts code points (`Array.from`, not `.slice`) so astral-plane characters
 * such as emoji are not split mid-character.
 */
const truncateSelectorText = (value) => {
    const codePoints = Array.from(value);
    if (codePoints.length <= LABEL_MAX_CODEPOINTS) {
        return value;
    }
    return `${codePoints.slice(0, LABEL_TRUNCATED_CODEPOINTS).join('')}...`;
};
/** Flatten newlines so a value never spans two lines of the fingerprint. */
const flattenNewlines = (value) => value.replace(/\n/g, ' ');
/**
 * Escape a value for a quoted `[field='...']` selector token: the token
 * delimiters and the escape character first, then the quote, so the quote's own
 * escaping backslash is not doubled by the delimiter pass.
 */
const escapeQuotedSelectorValue = (value) => (0, tokenEscape_1.escapeTokenValue)(value).replace(/'/g, "\\'");
/**
 * CSS-like selector string for a single mobile view, e.g.
 * `UIButton[role=control][label='Submit']`; `'unknown'` when no property was
 * emitted. When `normalizer` is `null` the inline transforms in this function
 * run in its place.
 */
const generateViewSelector = (view, config = ViewSelectorConfig.full(), normalizer = null) => {
    const normalize = (value, field) => normalizer === null
        ? value
        : normalizer.normalize(value, field);
    const parts = [];
    let className = '';
    if (config.includes('className')) {
        const raw = view.className;
        if (typeof raw === 'string' && raw.length > 0) {
            if (normalizer === null) {
                className = raw.split('.').pop();
            }
            else {
                className = normalize(raw, 'className');
            }
            if (className) {
                parts.push((0, tokenEscape_1.escapeTokenValue)(className));
            }
        }
    }
    if (config.includes('role')) {
        const calcProps = view.calculatedProps;
        if (calcProps &&
            typeof calcProps === 'object' &&
            calcProps !== null &&
            !Array.isArray(calcProps)) {
            const role = calcProps.role;
            if (typeof role === 'string' && role && role !== 'genericElement') {
                parts.push(`[role=${(0, tokenEscape_1.escapeTokenValue)(normalize(role, 'role'))}]`);
            }
        }
    }
    if (config.includes('accessibilityIdentifier')) {
        const accId = view.accessibilityIdentifier;
        if (accId) {
            parts.push(`[id=${(0, tokenEscape_1.escapeTokenValue)(normalize(String(accId), 'id'))}]`);
        }
    }
    if (config.includes('viewIdResourceName')) {
        const resName = view.viewIdResourceName;
        if (resName) {
            parts.push(`[res=${(0, tokenEscape_1.escapeTokenValue)(normalize(String(resName), 'res'))}]`);
        }
    }
    let label = null;
    if (config.includes('label')) {
        label = view.label || view.text;
        if (label &&
            config.identityBearingTextOnly &&
            !isIdentityBearingText(label)) {
            label = null;
        }
        if (label) {
            let labelText = typeof label === 'string' ? label : String(label);
            labelText = truncateSelectorText(labelText);
            labelText = flattenNewlines(labelText);
            labelText = normalize(labelText, 'label');
            labelText = escapeQuotedSelectorValue(labelText);
            parts.push(`[label='${labelText}']`);
            // The desc guard below must read the POST-transform label: if the
            // normalizer emptied it, desc is no longer a duplicate and is emitted.
            label = labelText;
        }
    }
    if (config.includes('contentDescription')) {
        const contentDesc = view.contentDescription;
        const descIsUsable = !config.identityBearingTextOnly || isIdentityBearingText(contentDesc);
        if (contentDesc && descIsUsable && !(config.includes('label') && label)) {
            let descText = typeof contentDesc === 'string' ? contentDesc : String(contentDesc);
            descText = truncateSelectorText(descText);
            descText = flattenNewlines(descText);
            descText = normalize(descText, 'desc');
            descText = escapeQuotedSelectorValue(descText);
            parts.push(`[desc='${descText}']`);
        }
    }
    if (config.includes('accessibilityTraits')) {
        const traits = view.accessibilityTraits;
        // An empty array is truthy in JS, so the length has to be checked
        // explicitly rather than relying on a plain truthiness test.
        const hasTraits = Array.isArray(traits)
            ? traits.length > 0
            : Boolean(traits);
        if (hasTraits) {
            let traitTokens;
            if (Array.isArray(traits)) {
                traitTokens = traits.map(t => String(t));
            }
            else if (typeof traits === 'string' && traits !== 'None') {
                if (traits.startsWith('[') && traits.endsWith(']')) {
                    traitTokens = traits
                        .slice(1, -1)
                        .split(',')
                        .map(t => t.trim());
                }
                else {
                    traitTokens = [traits];
                }
            }
            else {
                traitTokens = [];
            }
            // Sorted so the same element with differently-ordered stored traits
            // ([Button,Image] vs [Image,Button]) doesn't project to two distinct
            // fingerprints.
            const traitValues = traitTokens
                .filter(t => t && t !== 'None' && t !== 'Static Text')
                .sort();
            if (traitValues.length > 0) {
                const joined = (0, tokenEscape_1.escapeTokenValue)(normalize(traitValues.join(','), 'trait'));
                parts.push(`[trait=${joined}]`);
            }
        }
    }
    if (!className) {
        const elementType = view.elementType;
        if (elementType) {
            parts.push((0, tokenEscape_1.escapeTokenValue)(String(elementType)));
        }
    }
    return parts.length > 0 ? parts.join('') : 'unknown';
};
exports.generateViewSelector = generateViewSelector;
const formatAncestryPath = (path, separator = ' > ') => path.join(separator);
exports.formatAncestryPath = formatAncestryPath;
//# sourceMappingURL=selector.js.map